import { z } from 'zod'
import { PRIORITIES } from './domain.ts'

// Единый язык действий над задачей: им пользуются и бот (кнопки), и Mini App (POST /tasks/:id/actions).
// Все даты — ISO-8601 строки с часовым поясом (UTC 'Z' или смещение).

const isoDateTime = z.iso.datetime({ offset: true })
const reason = z.string().trim().min(1).max(1000)

/** Сколько ещё людей, кроме основного исполнителя, можно поставить на задачу. */
export const MAX_CO_ASSIGNEES = 10
/** Сколько подпунктов может быть у задачи. */
export const MAX_CHECKLIST_ITEMS = 30

export const SNOOZE_OPTIONS = ['1h', 'after_lunch', 'tomorrow'] as const
export type SnoozeOption = (typeof SNOOZE_OPTIONS)[number]

export const TaskEditPatch = z
  .object({
    title: z.string().trim().min(1).max(200),
    description: z.string().max(4000).nullable(),
    dod: z.string().max(300).nullable(),
    priority: z.enum(PRIORITIES),
    requiresAcceptance: z.boolean(),
    requiresResult: z.boolean(),
    projectId: z.number().int().positive().nullable(),
    /** Проверяющий (null — постановщик); назначен — проверка включается сама. */
    reviewerId: z.number().int().positive().nullable(),
  })
  .partial()
export type TaskEditPatch = z.infer<typeof TaskEditPatch>

export const TaskCommand = z.discriminatedUnion('type', [
  z.object({ type: z.literal('accept'), plannedStartAt: isoDateTime.optional() }),
  z.object({ type: z.literal('decline'), reason }),
  z.object({ type: z.literal('start') }),
  z.object({
    type: z.literal('submit'),
    resultText: z.string().max(4000).optional(),
    attachmentIds: z.array(z.number().int().positive()).max(20).optional(),
  }),
  z.object({ type: z.literal('verify') }),
  z.object({ type: z.literal('return'), comment: reason }),
  z.object({ type: z.literal('block'), reason }),
  z.object({ type: z.literal('unblock') }),
  z.object({ type: z.literal('on_track') }),
  z.object({ type: z.literal('snooze'), option: z.enum(SNOOZE_OPTIONS) }),
  z.object({
    type: z.literal('request_due'),
    proposedDueAt: isoDateTime,
    reason: z.string().max(1000).optional(),
  }),
  z.object({
    type: z.literal('decide_due'),
    requestId: z.number().int().positive(),
    approve: z.boolean(),
    note: z.string().max(1000).optional(),
  }),
  z.object({ type: z.literal('ask'), text: reason }),
  z.object({ type: z.literal('answer'), requestId: z.number().int().positive(), text: reason }),
  z.object({
    type: z.literal('set_due'),
    dueAt: isoDateTime.nullable(),
    dueKind: z.enum(['date', 'datetime']).optional(),
  }),
  z.object({ type: z.literal('edit'), patch: TaskEditPatch }),
  z.object({ type: z.literal('reassign'), assigneeId: z.number().int().positive() }),
  z.object({ type: z.literal('cancel'), reason: z.string().max(1000).optional() }),
  z.object({ type: z.literal('reopen') }),
  z.object({ type: z.literal('comment'), text: reason }),
  /** Соисполнители («достаточно одного»): полный список, кроме основного исполнителя. */
  z.object({
    type: z.literal('set_co_assignees'),
    userIds: z.array(z.number().int().positive()).max(MAX_CO_ASSIGNEES),
  }),
  z.object({
    type: z.literal('checklist_add'),
    text: z.string().trim().min(1).max(200),
    assigneeId: z.number().int().positive().nullish(),
  }),
  /** done не указан — переключить. */
  z.object({
    type: z.literal('checklist_toggle'),
    itemId: z.number().int().positive(),
    done: z.boolean().optional(),
  }),
  z.object({ type: z.literal('checklist_remove'), itemId: z.number().int().positive() }),
])
export type TaskCommand = z.infer<typeof TaskCommand>
export type TaskCommandType = TaskCommand['type']

export const TASK_COMMAND_TYPES = TaskCommand.options.map((o) => o.shape.type.value) as TaskCommandType[]

/** Создание задачи (из бота, Mini App, черновика ИИ или регулярного правила). */
/** Какой пост ждём и где: канал (null — любой подключённый) и описание или образец поста. */
export const PublishCheckInput = z.object({
  channelId: z.number().int().positive().nullish(),
  expected: z.string().trim().min(3).max(2000),
})
export type PublishCheckInput = z.infer<typeof PublishCheckInput>

export const CreateTaskInput = z.object({
  title: z.string().trim().min(1).max(200),
  description: z.string().max(4000).nullish(),
  dod: z.string().max(300).nullish(),
  assigneeId: z.number().int().positive(),
  /** Проект или клиент (метка). */
  projectId: z.number().int().positive().nullish(),
  /** Ещё исполнители — задача общая, закрыть её может любой из них. */
  coAssigneeIds: z.array(z.number().int().positive()).max(MAX_CO_ASSIGNEES).default([]),
  dueAt: isoDateTime.nullish(),
  dueKind: z.enum(['date', 'datetime']).default('datetime'),
  priority: z.enum(PRIORITIES).default('normal'),
  requiresAcceptance: z.boolean().default(false),
  /** Проверяющий: принимает работу вместо постановщика; задан — проверка включается сама. */
  reviewerId: z.number().int().positive().nullish(),
  requiresResult: z.boolean().default(false),
  watcherIds: z.array(z.number().int().positive()).max(20).default([]),
  /** Подпункты; assigneeId — за кем пункт (должен быть среди исполнителей, иначе добавится в них). */
  checklist: z
    .array(
      z.object({
        text: z.string().trim().min(1).max(200),
        assigneeId: z.number().int().positive().nullish(),
      }),
    )
    .max(MAX_CHECKLIST_ITEMS)
    .default([]),
  /** Проверить публикацию: бот ищет в канале пост, похожий на expected, и сам закрывает задачу. */
  publishCheck: PublishCheckInput.nullish(),
})
export type CreateTaskInput = z.input<typeof CreateTaskInput>
export type CreateTaskParsed = z.output<typeof CreateTaskInput>
